import React, { useEffect, useState } from "react";
import { t } from "ttag";
import { Button, BaseDialog, DeleteIcon, IconButton, Table, TextField } from "@coolremote/shared";
import styles from "./EnumMapEditor.module.css";
import { IEnumEntry } from "../types";

interface IProps {
  open: boolean;
  label: string;
  initialEntries: IEnumEntry[];
  onClose: () => void;
  onSave: (entries: IEnumEntry[]) => void;
  readOnly?: boolean;
}

const newEntry = (value = "", text = ""): IEnumEntry => ({ value, text });

const ensureSeed = (entries: IEnumEntry[]): IEnumEntry[] =>
  entries.length ? entries.map((e) => ({ ...e })) : [newEntry("0", "State 0"), newEntry("1", "State 1")];

const EnumMapEditor: React.FC<IProps> = ({ open, label, initialEntries, onClose, onSave, readOnly }) => {
  const [draft, setDraft] = useState<IEnumEntry[]>(ensureSeed(initialEntries));

  useEffect(() => {
    if (open) {
      setDraft(ensureSeed(initialEntries));
    }
  }, [open, initialEntries]);

  const updateField = (index: number, field: "value" | "text", value: string) => {
    setDraft((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = () => setDraft((rows) => [...rows, newEntry()]);

  const removeRow = (index: number) =>
    setDraft((rows) => {
      const next = rows.filter((_, i) => i !== index);
      return next.length ? next : [newEntry()];
    });

  const save = () => {
    const cleaned = draft.filter((entry) => entry.value !== "" || entry.text !== "");
    onSave(cleaned.length ? cleaned : [newEntry("0", "State 0")]);
  };
  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={`${label} ${t`Enumeration Map`}`}
      onConfirm={readOnly ? undefined : save}
      hideConfirm={readOnly}
      confirmLabel={t`Save Enumeration Map`}
      cancelLabel={readOnly ? t`Close` : t`Cancel`}
      size="medium"
    >
      <Table
        data={draft}
        getRowKey={(_, i) => i}
        columns={[
          {
            id: "value",
            title: t`VALUE`,
            width: "26%",
            render: (row: IEnumEntry, index: number) => (
              <TextField
                size="small"
                fullWidth
                disabled={readOnly}
                value={row.value}
                onChange={(v: string) => updateField(index, "value", v)}
              />
            ),
          },
          {
            id: "text",
            title: t`TEXT`,
            render: (row: IEnumEntry, index: number) => (
              <TextField
                size="small"
                fullWidth
                disabled={readOnly}
                value={row.text}
                onChange={(v: string) => updateField(index, "text", v)}
              />
            ),
          },
          ...(!readOnly ? [{
            id: "action",
            width: "12%",
            align: "center" as const,
            render: (_row: IEnumEntry, index: number) => (
              <IconButton onClick={() => removeRow(index)}>
                <DeleteIcon width={16} height={16} />
              </IconButton>
            ),
          }] : []),
        ]}
      />
      {!readOnly && (
        <div className={styles.addRow}>
          <Button white size="small" onClick={addRow}>
            + {t`Add State`}
          </Button>
        </div>
      )}
    </BaseDialog>
  );
};

export default EnumMapEditor;
